import React, { useMemo } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import Icon from 'fe-design-base/atoms/Icon';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import { times } from 'lodash';
import PropTypes from 'prop-types';

import { showExitWithoutSavingModal } from 'actions/modals';

import { cxHelpers } from 'util/className';

import { determineContext } from './utils';
const { cx, cxEl } = cxHelpers('TimecardNavigation');

const MAX_DOTS = 9;

const NavButton = ({ children, ...props }) => (
  <Box
    absolute
    top="50%"
    bgcolor="mono500"
    bradiusfull="50%"
    w={64}
    h={64}
    hcenter
    vcenter
    pointer
    {...props}
  >
    {children}
  </Box>
);

const NavigationDot = ({ active, i }) => (
  <Box
    bradiusfull
    bgcolor={active ? 'mono0' : 'mono400'}
    w={10}
    h={10}
    mh={5}
    className={cxEl('nav-dot', active ? `active-${i}` : null)}
  />
);

const Navigation = ({
  isLoading,
  shifts,
  isMobile,
  currentShift,
  setCurrentShiftId,
  changesMade,
  setChangesMade,
  onShowExitWithoutSavingModal,
  setEditMode,
}) => {
  const currentTimecardIndex = useMemo(
    () => shifts.findIndex(s => s.get('id') === currentShift.get('id')),
    [shifts, currentShift]
  );

  const isEmbedded = useIsEmbedded();

  if (shifts.count() <= 1) {
    return null;
  }

  function isPrevTimecardButtonVisible() {
    return (
      !isLoading &&
      shifts.count() > 1 &&
      currentShift.get('id') !== shifts.get(0).get('id')
    );
  }

  function isNextTimecardButtonVisible() {
    return (
      !isLoading &&
      shifts.count() > 1 &&
      currentShift.get('id') !== shifts.get(shifts.count() - 1).get('id')
    );
  }

  const handlePreviousTimecard = () => {
    setChangesMade(false);
    setCurrentShiftId(shifts.get(currentTimecardIndex - 1).get('id'));
    if (determineContext(window.location.pathname) === 'daily_review')
      setEditMode(true);
  };

  const handlePreviousTimecardWithChanges = () => {
    onShowExitWithoutSavingModal(handlePreviousTimecard);
  };

  const handleNextTimecard = () => {
    setChangesMade(false);
    setCurrentShiftId(shifts.get(currentTimecardIndex + 1).get('id'));
    if (determineContext(window.location.pathname) === 'daily_review')
      setEditMode(true);
  };

  const handleNextTimecardWithChanges = () => {
    onShowExitWithoutSavingModal(handleNextTimecard);
  };

  const rowsCount = shifts.count();
  let dotsCount = rowsCount;
  let activeIndex = currentTimecardIndex;

  if (dotsCount > MAX_DOTS) {
    const middleIndex = parseInt(MAX_DOTS / 2, 10);

    dotsCount = MAX_DOTS;

    if (activeIndex > middleIndex) {
      if (activeIndex >= rowsCount - MAX_DOTS + middleIndex) {
        activeIndex = dotsCount - (rowsCount - activeIndex);
      } else {
        activeIndex = middleIndex;
      }
    }
  }

  const leftNav = () => {
    const inputProps = {};
    if (isMobile) {
      inputProps.w = 24;
      inputProps.h = 24;
      inputProps.absolute = false;
      inputProps.ml = 11;
    } else if (isEmbedded) {
      inputProps.left = '15%';
    } else {
      inputProps.left = -88;
    }
    return (
      <NavButton
        onClick={
          changesMade
            ? handlePreviousTimecardWithChanges
            : handlePreviousTimecard
        }
        className={cxEl('prev-timecard')}
        {...inputProps}
      >
        <Icon size="medium" iconName="ArrowLeft" color="mono0" />
      </NavButton>
    );
  };

  const rightNav = () => {
    const inputProps = {};
    if (isMobile) {
      inputProps.w = 24;
      inputProps.h = 24;
      inputProps.absolute = false;
      inputProps.ml = 11;
    } else if (isEmbedded) {
      inputProps.right = '15%';
    } else {
      inputProps.right = -88;
    }
    return (
      <NavButton
        onClick={
          changesMade ? handleNextTimecardWithChanges : handleNextTimecard
        }
        className={cxEl('next-timecard')}
        {...inputProps}
      >
        <Icon size="medium" iconName="ArrowRight" color="mono0" />
      </NavButton>
    );
  };

  return (
    <Box className={cx()}>
      {!isMobile && isPrevTimecardButtonVisible() && leftNav()}
      {!isMobile && isNextTimecardButtonVisible() && rightNav()}
      <Box
        className={cxEl('nav-dots')}
        absolute
        bottom={isEmbedded ? '3%' : -35}
        left="50%"
      >
        <Box relative left="-50%" vcenter>
          {isMobile && isPrevTimecardButtonVisible() && leftNav()}
          {times(dotsCount, i => (
            <NavigationDot active={i === activeIndex} key={i} i={i} />
          ))}
          {isMobile && isNextTimecardButtonVisible() && rightNav()}
        </Box>
      </Box>
    </Box>
  );
};

Navigation.propTypes = {
  currentShift: ImmutablePropTypes.map.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setCurrentShiftId: PropTypes.func.isRequired,
  shifts: ImmutablePropTypes.list,
  isMobile: PropTypes.bool,
  changesMade: PropTypes.bool.isRequired,
  setChangesMade: PropTypes.func.isRequired,
  onShowExitWithoutSavingModal: PropTypes.func.isRequired,
};

export default connect(null, {
  onShowExitWithoutSavingModal: showExitWithoutSavingModal,
})(Navigation);
